import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import useInterval from '@framework/hooks/useInterval';
import { fetchQuery, getCachedValue, invalidateQueryKey, subscribe } from './queryCache';
import { exposeQueryCacheDebug } from './queryCacheDebug';

const DEFAULT_TTL_MS = 60_000;

export interface IUseQueryOptions {
  // Defaults to `fetcher.rpcMethod` (see BaseRepository.createRpcMethod), else an opaque per-fetcher key.
  queryKey?: string,
  enabled?: boolean,
  ttlMs?: number,
  cacheKey?: string,
  pollingIntervalMs?: number | null,
  onError?: (error: unknown) => void,
}

export interface IUseQueryResult<TResult> {
  data: TResult | undefined,
  error: string | null,
  isLoading: boolean,
  isFetching: boolean,
  isSuccess: boolean,
  isError: boolean,
  refetch: () => void,
}

interface ILoadOptions {
  skipCache?: boolean,
  isBackground?: boolean,
}

// Keyed by fetcher identity, not `.name`, which production builds can mangle.
const autoQueryKeys = new WeakMap<object, string>();
let autoQueryKeyCounter = 0;

const getAutoQueryKey = (fetcher: (...args: never[]) => unknown): string => {
  const { rpcMethod } = fetcher as unknown as { rpcMethod?: unknown };

  if (typeof rpcMethod === 'string') {
    return rpcMethod;
  }

  let key = autoQueryKeys.get(fetcher);

  if (!key) {
    key = `useQuery:auto:${ ++autoQueryKeyCounter }`;
    autoQueryKeys.set(fetcher, key);
  }

  return key;
};

const defaultOnError = (error: unknown) => AlertManager.error(getErrorMessage(error));

/**
 * Cached RPC query: refetches on payload change, supports polling and `invalidateQuery`.
 * See ./README.md.
 */
const useQuery = <TPayload, TResult>(
  fetcher: (payload: TPayload) => Promise<TResult>,
  payload: TPayload,
  options: IUseQueryOptions = {},
): IUseQueryResult<TResult> => {
  const {
    queryKey,
    enabled = true,
    ttlMs = DEFAULT_TTL_MS,
    cacheKey,
    pollingIntervalMs = null,
    onError = defaultOnError,
  } = options;

  const fullCacheKey = `${ queryKey ?? getAutoQueryKey(fetcher) }:${ cacheKey ?? JSON.stringify(payload) }`;

  const fetcherRef = useRef(fetcher);
  const payloadRef = useRef(payload);
  const onErrorRef = useRef(onError);
  const enabledRef = useRef(enabled);

  // Synced after commit, so a discarded render can't leak its payload into a later fetch.
  useLayoutEffect(() => {
    fetcherRef.current = fetcher;
    payloadRef.current = payload;
    onErrorRef.current = onError;
    enabledRef.current = enabled;
  });

  const [data, setData] = useState<TResult | undefined>(() => getCachedValue<TResult>(fullCacheKey));
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(() => enabled && data === undefined);
  const hasFetchedOnceRef = useRef(data !== undefined);
  const errorRef = useRef<string | null>(null);
  // A response whose id is no longer current was superseded and must not touch state.
  const requestIdRef = useRef(0);

  const load = useCallback(async ({ skipCache = false, isBackground = false }: ILoadOptions = {}) => {
    const requestId = ++requestIdRef.current;

    if (!skipCache) {
      const cached = getCachedValue<TResult>(fullCacheKey);

      if (cached !== undefined) {
        setData(cached);
        setError(null);
        setIsFetching(false);
        errorRef.current = null;
        hasFetchedOnceRef.current = true;
        return;
      }
    }

    setIsFetching(true);

    try {
      const response = await fetchQuery(fullCacheKey, () => fetcherRef.current(payloadRef.current), ttlMs);

      if (requestId !== requestIdRef.current) {
        return;
      }

      setData(response);
      setError(null);
      errorRef.current = null;
    } catch (e) {
      if (Dispatcher.isAbortError(e) || requestId !== requestIdRef.current) {
        return;
      }

      const message = getErrorMessage(e);

      // A poll failing with the same error again doesn't re-alert on every tick.
      if (!isBackground || message !== errorRef.current) {
        onErrorRef.current(e);
      }

      setError(message);
      errorRef.current = message;
    } finally {
      if (requestId === requestIdRef.current) {
        hasFetchedOnceRef.current = true;
        setIsFetching(false);
      }
    }
  }, [fullCacheKey, ttlMs]);

  useEffect(() => {
    exposeQueryCacheDebug();
  }, []);

  useEffect(() => {
    if (enabled) {
      load();
    }
  }, [fullCacheKey, enabled, load]);

  useEffect(() => subscribe(fullCacheKey, () => {
    if (enabledRef.current) {
      load();
    }
  }), [fullCacheKey, load]);

  useInterval(() => load({ skipCache: true, isBackground: true }), enabled ? pollingIntervalMs : null);

  const refetch = useCallback(() => {
    invalidateQueryKey(fullCacheKey);

    if (!enabledRef.current) {
      load();
    }
  }, [fullCacheKey, load]);

  return {
    data,
    error,
    isLoading: isFetching && !hasFetchedOnceRef.current,
    isFetching,
    isSuccess: data !== undefined && error === null,
    isError: error !== null,
    refetch,
  };
};

export default useQuery;
